import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { WorkflowExecutionDetail } from '../../types/workflow-inspection.types';
import { JsonViewer } from '../json-viewer';

/**
 * Metadata (from describe): Search Attributes, Memo and User Metadata in one
 * tab instead of Temporal's three — each is usually a handful of keys.
 */
export function MetadataTab({ detail }: { detail: WorkflowExecutionDetail }) {
  const { t } = useTranslation('workflow-inspection');
  const userMeta = detail.userMetadata;

  return (
    <div
      className="grid grid-cols-1 gap-4 lg:grid-cols-2"
      data-testid="workflow-inspector-metadata"
    >
      <Block
        title={t('metadata.searchAttributes')}
        value={detail.searchAttributes}
        testId="workflow-inspector-metadata-search-attributes"
      />
      <Block
        title={t('metadata.memo')}
        value={detail.memo}
        testId="workflow-inspector-metadata-memo"
      />
      <section
        className="space-y-1.5 lg:col-span-2"
        data-testid="workflow-inspector-metadata-user-metadata"
      >
        <h4 className="text-foreground text-sm font-semibold">{t('metadata.userMetadata')}</h4>
        {userMeta && (userMeta.summary || userMeta.details) ? (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Labelled
              label={t('metadata.summary')}
              value={userMeta.summary}
              testId="workflow-inspector-metadata-summary"
            />
            <Labelled
              label={t('metadata.details')}
              value={userMeta.details}
              testId="workflow-inspector-metadata-details"
            />
          </div>
        ) : (
          <None />
        )}
      </section>
    </div>
  );
}

/** One titled JSON block; `testId` tags the section and its viewer (`{testId}-json`). */
function Block({
  title,
  value,
  testId,
}: {
  title: string;
  value: Record<string, unknown>;
  testId: string;
}) {
  return (
    <section className="min-w-0 space-y-1.5" data-testid={testId}>
      <h4 className="text-foreground text-sm font-semibold">{title}</h4>
      {Object.keys(value).length > 0 ? (
        <JsonViewer value={value} title={title} testId={`${testId}-json`} />
      ) : (
        <None />
      )}
    </section>
  );
}

/** Labelled user-metadata value; `testId` tags the wrapper and its viewer (`{testId}-json`). */
function Labelled({
  label,
  value,
  testId,
}: {
  label: string;
  value: unknown;
  testId: string;
}): ReactNode {
  return (
    <div className="min-w-0 space-y-1" data-testid={testId}>
      <p className="text-muted-foreground text-xs">{label}</p>
      {value === null || value === undefined ? (
        <None />
      ) : typeof value === 'string' ? (
        <p className="text-foreground text-sm whitespace-pre-wrap">{value}</p>
      ) : (
        <JsonViewer value={value} title={label} testId={`${testId}-json`} />
      )}
    </div>
  );
}

function None() {
  const { t } = useTranslation('workflow-inspection');
  return (
    <p
      className="text-muted-foreground bg-muted/30 rounded-lg border p-3 text-sm"
      data-testid="workflow-inspector-metadata-empty"
    >
      {t('metadata.none')}
    </p>
  );
}
